<template>
  <div ref="chartDomRef"></div>
</template>

<script setup>
import unitHelper from '@client/helper/unit';
import { usePakHo } from '@client/pages/activity-detail/hooks/usePakHo';
import { xAxisKeyMap } from '@client/store/activityDetailStore';
import { getCurrentInstance, nextTick, ref, watch } from 'vue';
import { useActiveAttributes } from '../useActiveAttributes';

import useDrawChart from '../useDrawChart';
import useFrameSelection from '../useFrameSelection';
import useTimeChart from '../useTimeChart';
import useXAxisConfig from '../useXAxisConfig';
import { TimeChart } from './chart';
import { useListenResize } from '@client/hooks/useBoardResizeEvent';
import { onBeforeUnmount } from 'vue';
import { initStore } from '@client/store/initStore';
import { activityDetailStore } from '@client/store/activityDetailStore';

import * as corosSportConfig from '@coros/sport-config';
const { INDOOR_CLIMB_BOULDERING_CHART_NAME } = corosSportConfig.sport.climb;

const vm = getCurrentInstance();
const t = vm.proxy.$root.$t;

const initStoreRef = initStore();
const activityDetailStoreRef = activityDetailStore();
const THEME = initStoreRef.theme;

const chartDataType = 'portion';
const xAxisKey = xAxisKeyMap.time;
const chartRef = ref(null);
const chartDomRef = ref(null);

// 将x坐标的值转换为对应的文案
function xTickInfo(millisecond) {
  const second = parseInt(millisecond / 100);

  return {
    text: unitHelper.s2hmsStr(second, ':'),
    value: millisecond
  };
}

const { pauseList, tryToDrawChart, chartData } = useDrawChart({ drawChart });
const { createSelectionZone } = useFrameSelection({ chartDataType, chartRef, tryToDrawChart });
const { xAxisConfig } = useXAxisConfig({ isPortion: true });
const { sportAttrs, chartAttr, activeAttributes } = useActiveAttributes();

const { chartNames, processAttributes, createMaskLayer } = useTimeChart({
  activeAttributes,
  chartRef
});
watch(activeAttributes, () =>
  nextTick(() => {
    tryToDrawChart();
  })
);

const { cancelListner } = useListenResize(() => {
  tryToDrawChart();
});
onBeforeUnmount(cancelListner);

// 绘制图表
function drawChart() {
  chartRef.value?.remove();

  let left = 0;
  let right = 0;
  let chartCount = chartNames.value.length;
  if (chartNames.value.includes('Air_Power')) {
    chartCount--;
  }

  switch (chartCount) {
    case 1:
      left = 40;
      right = 30;
      break;
    case 2:
      left = 40;
      right = 40;
      break;
    case 3:
      left = 85;
      right = 40;
      break;
    case 4:
      left = 85;
      right = 85;
      break;
  }

  if (chartCount > 4) {
    left = 85;
    right = 85;
  }

  // 难度图表有图例，top margin 要加大
  const hasGradingChart = sportAttrs.value.find((i) => i.chartName === INDOOR_CLIMB_BOULDERING_CHART_NAME);
  chartRef.value = new TimeChart({
    el: chartDomRef.value,
    theme: THEME,
    recommandLabel: t('S4493'),
    margin: {
      top: hasGradingChart ? 40 : 20,
      bottom: 22,
      left,
      right
    },
    // x轴使用的属性字段名
    xAxisKey,
    // x轴刻度显示的文本信息转换方法，不传则直接使用属性值
    xTickInfo: xTickInfo,
    xTickLineSize: 0,
    showYLine: false,
    crossHairCallback: crossHairCallback
  });

  const dataList = chartData.value;
  const startX = xAxisConfig.value.startX;
  const endX = xAxisConfig.value.endX;
  const xTicks = xAxisConfig.value.xTicks;

  // 注入数据
  chartRef.value.setData(dataList);
  // 创建x轴比例尺
  chartRef.value.createXAxis([startX, endX]);
  // 绘制x轴
  chartRef.value.drawXScale(xTicks);
  // 设置显示的属性
  setShowingAttributes();
  // 生成暂停虚线
  chartRef.value.drawPauseLine(pauseList.value);
  // 绘制圈速表选中对应的遮罩
  createMaskLayer();
  // 绘制框选区遮罩
  createSelectionZone();

  nextTick(() => {
    chartRef.value.el.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
    chartRef.value.el.addEventListener('touchstart', (e) => {
      e.preventDefault();
    });
  });
}

function setShowingAttributes() {
  const attributes2 = processAttributes(chartAttr.value);
  attributes2.forEach((item) => {
    if (item.chartName === INDOOR_CLIMB_BOULDERING_CHART_NAME) {
      item.gradeStyles = activityDetailStoreRef.gradeStyles;
    }
  });
  chartRef.value.setShowingAttributes(attributes2);
}

const { crossHairMove } = usePakHo();
function crossHairCallback(e, data) {
  // 暂停区间补充的空数据不传到外部
  if (data.isPauseZoneNullData) {
    return;
  }
  crossHairMove(unitHelper.clone(data));
}
</script>
